import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  runTransaction,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../../../shared/storage/firebase';
import { StarTransaction, LessonStars, StarReason, User, MuseUpgradeTransaction, MuseTier, MUSE_UPGRADE_COSTS } from '../../../types';

export class StarService {
  private static readonly STAR_TRANSACTIONS_COLLECTION = 'starTransactions';
  private static readonly LESSON_STARS_COLLECTION = 'lessonStars';
  private static readonly MUSE_UPGRADE_TRANSACTIONS_COLLECTION = 'museUpgradeTransactions';
  private static readonly USERS_COLLECTION = 'users';
  private static readonly MAX_STARS_PER_LESSON = 3;

  /**
   * Awards a star to a student with comprehensive validation and atomic operations
   */
  static async giveStarToStudent(
    studentId: string,
    teacherId: string,
    squadId: string,
    reason: StarReason,
    customReason: string = ''
  ): Promise<{ success: boolean; message: string; starTransaction?: StarTransaction }> {
    try {
      const lessonDate = this.getCurrentLessonDate();
      const lessonStarsId = this.generateLessonStarsId(studentId, lessonDate, squadId);

      // Use Firestore transaction for atomic operations
      const result = await runTransaction(db, async (transaction) => {
        // ALL READS MUST COME FIRST

        // 1. Read current lesson stars
        const lessonStarsRef = doc(db, this.LESSON_STARS_COLLECTION, lessonStarsId);
        const lessonStarsDoc = await transaction.get(lessonStarsRef);

        // 2. Read user's current star balance
        const userRef = doc(db, this.USERS_COLLECTION, studentId);
        const userDoc = await transaction.get(userRef);

        if (!userDoc.exists()) {
          throw new Error('Student not found');
        }

        // Process read data
        let currentStarsReceived = 0;
        let existingTransactions: string[] = [];

        if (lessonStarsDoc.exists()) {
          const data = lessonStarsDoc.data() as LessonStars;
          currentStarsReceived = data.starsReceived;
          existingTransactions = data.transactions;
        }

        // Validate star limit
        if (currentStarsReceived >= this.MAX_STARS_PER_LESSON) {
          throw new Error(`Student has already received maximum ${this.MAX_STARS_PER_LESSON} stars for this lesson`);
        }

        const userData = userDoc.data() as User;
        const currentStarBalance = userData.stars || 0;

        // NOW ALL WRITES

        // 1. Create star transaction
        const starTransactionRef = doc(collection(db, this.STAR_TRANSACTIONS_COLLECTION));
        const starTransactionData: any = {
          id: starTransactionRef.id,
          studentId,
          teacherId,
          squadId,
          reason,
          timestamp: Timestamp.now(),
          lessonDate,
          amount: 1
        };

        // Only add customReason if it exists and reason is 'other'
        if (reason === 'other' && customReason && customReason.trim() !== '') {
          starTransactionData.customReason = customReason.trim();
        }

        transaction.set(starTransactionRef, starTransactionData);

        // 2. Update lesson stars record
        const updatedLessonStars: LessonStars = {
          id: lessonStarsId,
          studentId,
          squadId,
          lessonDate,
          starsReceived: currentStarsReceived + 1,
          transactions: [...existingTransactions, starTransactionRef.id],
          lastUpdated: Timestamp.now()
        };

        transaction.set(lessonStarsRef, updatedLessonStars);

        // 3. Update user's star balance
        transaction.update(userRef, {
          stars: currentStarBalance + 1
        });

        return { starTransaction: starTransactionData as StarTransaction, message: 'Star awarded successfully!' };
      });

      return {
        success: true,
        message: result.message,
        starTransaction: result.starTransaction
      };

    } catch (error) {
      console.error('Error giving star to student:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to award star'
      };
    }
  }

  /**
   * Gets the current star balance for a student
   */
  static async getStudentStarBalance(studentId: string): Promise<number> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, studentId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Student not found');
      }

      const userData = userDoc.data() as User;
      return userData.stars || 0;
    } catch (error) {
      console.error('Error getting student star balance:', error);
      return 0;
    }
  }

  /**
   * Gets complete star transaction history for a student
   */
  static async getStudentStarHistory(
    studentId: string,
    limitCount = 50
  ): Promise<StarTransaction[]> {
    try {
      const q = query(
        collection(db, this.STAR_TRANSACTIONS_COLLECTION),
        where('studentId', '==', studentId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as StarTransaction[];
    } catch (error) {
      console.error('Error getting student star history:', error);
      return [];
    }
  }

  /**
   * Gets star information for a specific lesson
   */
  static async getLessonStars(
    studentId: string,
    squadId: string,
    lessonDate?: string
  ): Promise<LessonStars | null> {
    try {
      const dateToUse = lessonDate || this.getCurrentLessonDate();
      const lessonStarsId = this.generateLessonStarsId(studentId, dateToUse, squadId);

      const lessonStarsRef = doc(db, this.LESSON_STARS_COLLECTION, lessonStarsId);
      const lessonStarsDoc = await getDoc(lessonStarsRef);

      if (!lessonStarsDoc.exists()) {
        return null;
      }

      return lessonStarsDoc.data() as LessonStars;
    } catch (error) {
      console.error('Error getting lesson stars:', error);
      return null;
    }
  }

  /**
   * Checks if a student can receive more stars for the current lesson
   */
  static async canReceiveMoreStars(
    studentId: string,
    squadId: string,
    lessonDate?: string
  ): Promise<{ canReceive: boolean; starsReceived: number; starsRemaining: number }> {
    try {
      const lessonStars = await this.getLessonStars(studentId, squadId, lessonDate);
      const starsReceived = lessonStars?.starsReceived || 0;
      const starsRemaining = this.MAX_STARS_PER_LESSON - starsReceived;

      return {
        canReceive: starsRemaining > 0,
        starsReceived,
        starsRemaining
      };
    } catch (error) {
      console.error('Error checking if student can receive more stars:', error);
      return {
        canReceive: false,
        starsReceived: 0,
        starsRemaining: 0
      };
    }
  }

  /**
   * Gets stars for all students in a squad for the current lesson
   */
  static async getSquadLessonStars(
    squadId: string,
    lessonDate?: string
  ): Promise<Record<string, LessonStars>> {
    try {
      const dateToUse = lessonDate || this.getCurrentLessonDate();

      const q = query(
        collection(db, this.LESSON_STARS_COLLECTION),
        where('squadId', '==', squadId),
        where('lessonDate', '==', dateToUse)
      );

      const querySnapshot = await getDocs(q);
      const result: Record<string, LessonStars> = {};

      querySnapshot.docs.forEach(doc => {
        const data = doc.data() as LessonStars;
        result[data.studentId] = data;
      });

      return result;
    } catch (error) {
      console.error('Error getting squad lesson stars:', error);
      return {};
    }
  }

  /**
   * Spends stars from a student's balance (for future muse upgrades)
   */
  static async spendStars(
    studentId: string,
    amount: number,
    reason: string
  ): Promise<{ success: boolean; message: string; newBalance?: number }> {
    try {
      const result = await runTransaction(db, async (transaction) => {
        const userRef = doc(db, this.USERS_COLLECTION, studentId);
        const userDoc = await transaction.get(userRef);

        if (!userDoc.exists()) {
          throw new Error('Student not found');
        }

        const userData = userDoc.data() as User;
        const currentStarBalance = userData.stars || 0;

        if (currentStarBalance < amount) {
          throw new Error(`Insufficient stars. Current balance: ${currentStarBalance}, Required: ${amount}`);
        }

        const newBalance = currentStarBalance - amount;
        transaction.update(userRef, { stars: newBalance });

        // TODO: Log spending transaction for future features
        console.log(`Student ${studentId} spent ${amount} stars for ${reason}. New balance: ${newBalance}`);

        return { newBalance };
      });

      return {
        success: true,
        message: `Successfully spent ${amount} stars`,
        newBalance: result.newBalance
      };

    } catch (error) {
      console.error('Error spending stars:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to spend stars'
      };
    }
  }

  /**
   * Helper: Get current lesson date in YYYY-MM-DD format
   */
  private static getCurrentLessonDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Helper: Generate consistent lesson stars document ID
   */
  private static generateLessonStarsId(
    studentId: string,
    lessonDate: string,
    squadId: string
  ): string {
    return `${studentId}_${lessonDate}_${squadId}`;
  }

  /**
   * Helper: Get user-friendly reason display text
   */
  static getReasonDisplayText(reason: StarReason, customReason?: string): string {
    if (reason === 'other' && customReason) {
      return customReason;
    }

    const reasonMap: Record<StarReason, string> = {
      creativity: 'Creativity',
      teamwork: 'Teamwork',
      leadership: 'Leadership',
      kindness: 'Kindness',
      persistence: 'Persistence',
      focus: 'Focus',
      positivity: 'Positivity',
      other: 'Other'
    };

    return reasonMap[reason] || reason;
  }

  /**
   * Helper: Get all available star reasons for UI
   */
  static getAvailableReasons(): { value: StarReason; label: string }[] {
    return [
      { value: 'creativity', label: 'Creativity' },
      { value: 'teamwork', label: 'Teamwork' },
      { value: 'leadership', label: 'Leadership' },
      { value: 'kindness', label: 'Kindness' },
      { value: 'persistence', label: 'Persistence' },
      { value: 'focus', label: 'Focus' },
      { value: 'positivity', label: 'Positivity' },
      { value: 'other', label: 'Other' }
    ];
  }

  /**
   * MUSE UPGRADE SYSTEM
   */

  /**
   * Upgrades a muse to the next tier using stars
   */
  static async upgradeMuse(
    museName: string,
    fromTier: MuseTier,
    toTier: MuseTier,
    starsCost: number,
    userId?: string
  ): Promise<{ success: boolean; message: string; upgradeTransaction?: MuseUpgradeTransaction }> {
    try {
      // Use provided userId or try to get from auth
      let userIdToUse: string;
      if (userId) {
        userIdToUse = userId;
      } else {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          return { success: false, message: 'User not authenticated' };
        }
        userIdToUse = currentUser.uid;
      }

      // Validate upgrade path
      if (fromTier >= toTier) {
        return { success: false, message: 'Invalid upgrade path' };
      }

      if (toTier > 3) {
        return { success: false, message: 'Maximum tier is 3' };
      }

      // Validate cost
      const expectedCost = this.getUpgradeCost(fromTier, toTier);
      if (starsCost !== expectedCost) {
        return { success: false, message: 'Invalid upgrade cost' };
      }

      // Use Firestore transaction for atomic operations
      const result = await runTransaction(db, async (transaction) => {
        // Read user's current star balance
        const userRef = doc(db, this.USERS_COLLECTION, userIdToUse);
        const userDoc = await transaction.get(userRef);

        if (!userDoc.exists()) {
          throw new Error('User not found');
        }

        const userData = userDoc.data() as User;
        const currentStarBalance = userData.stars || 0;

        // Check if user can afford the upgrade
        if (currentStarBalance < starsCost) {
          throw new Error(`Insufficient stars. Current balance: ${currentStarBalance}, Required: ${starsCost}`);
        }

        // Create upgrade transaction record
        const upgradeTransactionRef = doc(collection(db, this.MUSE_UPGRADE_TRANSACTIONS_COLLECTION));
        const upgradeTransactionData: MuseUpgradeTransaction = {
          id: upgradeTransactionRef.id,
          userId: userIdToUse,
          museName,
          fromTier,
          toTier,
          starsCost,
          timestamp: Timestamp.now()
        };

        // Deduct stars from user balance
        const newStarBalance = currentStarBalance - starsCost;

        // Write operations
        transaction.set(upgradeTransactionRef, upgradeTransactionData);
        transaction.update(userRef, { stars: newStarBalance });

        // Update the muse tier in user's muses array
        if (userData.muses) {
          const updatedMuses = userData.muses.map(muse =>
            muse.name === museName
              ? { ...muse, tier: toTier }
              : muse
          );
          transaction.update(userRef, { muses: updatedMuses });
        }

        return { upgradeTransaction: upgradeTransactionData, message: 'Muse upgraded successfully!' };
      });

      return {
        success: true,
        message: result.message,
        upgradeTransaction: result.upgradeTransaction
      };

    } catch (error) {
      console.error('Error upgrading muse:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to upgrade muse'
      };
    }
  }

  /**
   * Gets the cost in stars to upgrade from one tier to another
   */
  static getUpgradeCost(fromTier: MuseTier, toTier: MuseTier): number {
    if (fromTier === 1 && toTier === 2) {
      return MUSE_UPGRADE_COSTS.TIER_2;
    }
    if (fromTier === 2 && toTier === 3) {
      return MUSE_UPGRADE_COSTS.TIER_3;
    }
    return 0; // Invalid upgrade path
  }

  /**
   * Gets upgrade history for a user
   */
  static async getUserUpgradeHistory(
    userId: string,
    limitCount = 20
  ): Promise<MuseUpgradeTransaction[]> {
    try {
      const q = query(
        collection(db, this.MUSE_UPGRADE_TRANSACTIONS_COLLECTION),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as MuseUpgradeTransaction[];
    } catch (error) {
      console.error('Error getting upgrade history:', error);
      return [];
    }
  }

  /**
   * Gets total stats for muse upgrades by a user
   */
  static async getUserUpgradeStats(userId: string): Promise<{
    totalUpgrades: number;
    totalStarsSpent: number;
    tier2Upgrades: number;
    tier3Upgrades: number;
  }> {
    try {
      const upgrades = await this.getUserUpgradeHistory(userId, 1000); // Get all upgrades

      const stats = {
        totalUpgrades: upgrades.length,
        totalStarsSpent: upgrades.reduce((sum, upgrade) => sum + upgrade.starsCost, 0),
        tier2Upgrades: upgrades.filter(upgrade => upgrade.toTier === 2).length,
        tier3Upgrades: upgrades.filter(upgrade => upgrade.toTier === 3).length
      };

      return stats;
    } catch (error) {
      console.error('Error getting upgrade stats:', error);
      return {
        totalUpgrades: 0,
        totalStarsSpent: 0,
        tier2Upgrades: 0,
        tier3Upgrades: 0
      };
    }
  }

  /**
   * Helper: Get tier display name
   */
  static getTierDisplayName(tier: MuseTier): string {
    switch (tier) {
      case 1: return 'Normal';
      case 2: return 'Shiny';
      case 3: return 'Chrome';
      default: return 'Unknown';
    }
  }

  /**
   * Helper: Check if a muse can be upgraded
   */
  static canUpgradeMuse(currentTier?: MuseTier): boolean {
    const tier = currentTier || 1;
    return tier < 3; // Can upgrade if not already at max tier
  }

  /**
   * Helper: Get next tier for a muse
   */
  static getNextTier(currentTier?: MuseTier): MuseTier | null {
    const tier = currentTier || 1;
    if (tier < 3) {
      return (tier + 1) as MuseTier;
    }
    return null; // Already at max tier
  }
}